import React from "react";
import {
  MdReceipt,
  MdCreditCard,
  MdStore,
  MdCheckCircle,
  MdCancel,
  MdAutorenew,
  MdCalendarToday,
  MdShoppingCart,
  MdSchedule,
} from "react-icons/md";
import { SiStripe, SiAppstore } from "react-icons/si";
import {
  StripePurchase,
  RevenueCatSubscription,
  RevenueCatTransaction,
  TransactionDiscount,
  UpcomingPlan,
} from "../../types/financial";
import {
  formatBillingPeriod,
  formatDiscountAmount,
  formatDiscountReason,
  formatMoney,
  formatProductName,
} from "../../utils/formatters";
import ModalShell from "../modals/ModalShell";
import { badge } from "../../utils/badgeColors";
import { parseApiDate } from "../../utils/dateUtils";
import { EMPTY_VALUE, formatLabel } from "../../utils/formatters";

interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: {
    type: "purchase" | "subscription";
    provider: "stripe" | "revenuecat";
    original:
      | StripePurchase
      | RevenueCatSubscription
      | RevenueCatTransaction
      | UpcomingPlan;
  } | null;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  isOpen,
  onClose,
  transaction,
}) => {
  if (!isOpen || !transaction) return null;

  const formatDate = (dateString: string) => {
    const date = parseApiDate(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatStoreLabel = (store: string) => {
    return store
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  };

  const renderPromoCode = (discount?: TransactionDiscount | null) =>
    discount ? (
      <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1.5 text-sm font-semibold text-green-800 dark:bg-green-800 dark:text-green-100">
        <MdCheckCircle className="mr-1 h-4 w-4" />
        {discount.code ?? formatDiscountReason(discount)}
      </span>
    ) : (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
        <MdCancel className="mr-1 h-4 w-4" />
        Not used
      </span>
    );

  const renderStripeDetails = (purchase: StripePurchase) => {
    const discount = purchase.discount;
    const reportsDiscount = discount !== undefined;
    const originalPrice = formatMoney(
      discount?.original_amount,
      purchase.currency
    );
    const discountAmount = formatDiscountAmount(discount);

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="border-b border-gray-200 pb-4 dark:border-gray-700">
          <div className="mb-2 flex items-center gap-2">
            <SiStripe className="h-6 w-6 text-purple-600" />
            <h3 className="text-xl font-bold text-navy-700 dark:text-white">
              Stripe Purchase Details
            </h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Transaction ID: {purchase.id}
          </p>
        </div>

        {/* Product Information */}
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
            Product Information
          </h4>
          <div className="space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            <div>
              <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Product Name
              </div>
              <div className="font-medium text-navy-700 dark:text-white">
                {purchase.product_name}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Product ID
                </div>
                <div className="font-mono text-sm text-navy-700 dark:text-white">
                  {purchase.product_id}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Price ID
                </div>
                <div className="font-mono text-sm text-navy-700 dark:text-white">
                  {purchase.price_id}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Information */}
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
            Payment Information
          </h4>
          <div className="space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Amount paid
                </div>
                <div className="text-2xl font-bold text-navy-700 dark:text-white">
                  ${purchase.amount.toFixed(2)}{" "}
                  {purchase.currency.toUpperCase()}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Status
                </div>
                <div>
                  {purchase.status === "succeeded" ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1.5 text-sm font-semibold text-green-800 dark:bg-green-800 dark:text-green-100">
                      <MdCheckCircle className="mr-1 h-4 w-4" />
                      Succeeded
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1.5 text-sm font-semibold text-red-800 dark:bg-red-800 dark:text-red-100">
                      <MdCancel className="mr-1 h-4 w-4" />
                      {purchase.status}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {reportsDiscount && (
              <div className="grid grid-cols-2 gap-4">
                {/* Empty cell keeps Promo code under Status at full price */}
                <div>
                  {originalPrice && (
                    <>
                      <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                        Original price
                      </div>
                      <div className="font-medium text-gray-500 line-through dark:text-gray-400">
                        {originalPrice}
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                    Promo code
                  </div>
                  {renderPromoCode(discount)}
                </div>
              </div>
            )}

            {discountAmount && (
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Discount
                </div>
                <div className="font-medium text-green-700 dark:text-green-400">
                  {discountAmount}
                </div>
              </div>
            )}

            {purchase?.payment_method && purchase?.payment_method.brand && (
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Payment Method
                </div>
                <div className="inline-flex items-center rounded bg-gray-100 px-3 py-2 text-sm font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                  <MdCreditCard className="mr-2 h-4 w-4" />
                  {purchase.payment_method.brand.charAt(0).toUpperCase() +
                    purchase.payment_method.brand.slice(1)}{" "}
                  •••• {purchase.payment_method.last4}
                </div>
              </div>
            )}

            <div>
              <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Payment Date
              </div>
              <div className="flex items-center text-navy-700 dark:text-white">
                <MdCalendarToday className="mr-2 h-4 w-4 text-gray-400" />
                {formatDate(purchase.created_at)}
              </div>
            </div>

            {purchase.receipt_number && (
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Receipt Number
                </div>
                <div className="font-mono text-sm text-navy-700 dark:text-white">
                  {purchase.receipt_number}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Refund Information */}
        {(purchase.is_refunded || purchase.amount_refunded > 0) && (
          <div>
            <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
              Refund Information
            </h4>
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-900/20">
              <div className="flex items-start gap-3">
                <MdCancel className="mt-0.5 h-5 w-5 text-orange-600 dark:text-orange-400" />
                <div>
                  <div className="mb-1 font-medium text-orange-900 dark:text-orange-100">
                    This transaction has been refunded
                  </div>
                  <div className="text-sm text-orange-700 dark:text-orange-300">
                    Refunded Amount: ${purchase.amount_refunded.toFixed(2)}{" "}
                    {purchase.currency.toUpperCase()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Receipt Link */}
        {purchase.receipt_url && (
          <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
            <a
              href={purchase.receipt_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center rounded-md bg-biafit-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-biafit-secondary"
            >
              <MdReceipt className="mr-2 h-5 w-5" />
              View Stripe Receipt
            </a>
          </div>
        )}
      </div>
    );
  };

  const renderRevenueCatDetails = (subscription: RevenueCatSubscription) => {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="border-b border-gray-200 pb-4 dark:border-gray-700">
          <div className="mb-2 flex items-center gap-2">
            <SiAppstore className="h-6 w-6 text-blue-600" />
            <h3 className="text-xl font-bold text-navy-700 dark:text-white">
              In-App Subscription Details
            </h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Transaction ID: {subscription.original_transaction_id}
          </p>
        </div>

        {/* Subscription Information */}
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
            Subscription Information
          </h4>
          <div className="space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            <div>
              <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Product ID
              </div>
              <div className="font-medium text-navy-700 dark:text-white">
                {formatProductName(subscription.product_id)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Store
                </div>
                <div className="inline-flex items-center rounded bg-blue-100 px-3 py-1.5 text-sm font-medium text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                  <MdStore className="mr-1.5 h-4 w-4" />
                  {formatStoreLabel(subscription.store)}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Environment
                </div>
                <div className="inline-flex items-center rounded bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                  {subscription.environment}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Price
                </div>
                <div className="text-xl font-bold text-navy-700 dark:text-white">
                  ${parseFloat(subscription.price).toFixed(2)}{" "}
                  {subscription.currency}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Status
                </div>
                <div>
                  {subscription.status === "active" ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1.5 text-sm font-semibold text-green-800 dark:bg-green-800 dark:text-green-100">
                      <MdCheckCircle className="mr-1 h-4 w-4" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-800 dark:bg-gray-800 dark:text-gray-100">
                      {subscription.status}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
            Important Dates
          </h4>
          <div className="space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            <div>
              <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Purchased At
              </div>
              <div className="flex items-center text-navy-700 dark:text-white">
                <MdCalendarToday className="mr-2 h-4 w-4 text-gray-400" />
                {formatDate(subscription.purchased_at)}
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Expires At
              </div>
              <div className="flex items-center text-navy-700 dark:text-white">
                <MdCalendarToday className="mr-2 h-4 w-4 text-gray-400" />
                {formatDate(subscription.expires_at)}
              </div>
            </div>
            {subscription.canceled_at && (
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Canceled At
                </div>
                <div className="flex items-center text-red-600 dark:text-red-400">
                  <MdCancel className="mr-2 h-4 w-4" />
                  {formatDate(subscription.canceled_at)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Renewal Information */}
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
            Renewal Information
          </h4>
          <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <MdAutorenew
                className={`h-6 w-6 ${
                  subscription.will_renew ? "text-green-600" : "text-gray-400"
                }`}
              />
              <div>
                <div className="font-medium text-navy-700 dark:text-white">
                  {subscription.will_renew
                    ? "Auto-Renewal Enabled"
                    : "Auto-Renewal Disabled"}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {subscription.will_renew
                    ? "This subscription will automatically renew at expiration"
                    : "This subscription will not renew"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Entitlements */}
        {subscription.entitlement_ids &&
          subscription.entitlement_ids.length > 0 && (
            <div>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                Entitlements
              </h4>
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                <div className="flex flex-wrap gap-2">
                  {subscription.entitlement_ids.map((entitlement, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center rounded-full bg-green-100 px-3 py-1.5 text-sm font-medium text-green-800 dark:bg-green-800 dark:text-green-100"
                    >
                      <MdCheckCircle className="mr-1.5 h-4 w-4" />
                      {entitlement}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

        {/* Offering Information */}
        {subscription.presented_offering_id && (
          <div>
            <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
              Offering Information
            </h4>
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
              <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Presented Offering ID
              </div>
              <div className="font-mono text-sm text-navy-700 dark:text-white">
                {subscription.presented_offering_id}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // One store transaction — a purchase or a renewal — fetched live from RevenueCat. It
  // carries none of the chain-level facts (renewal, entitlements, cancellation) the stored
  // subscription branch above reads, so it gets its own layout.
  const renderRevenueCatTransactionDetails = (tx: RevenueCatTransaction) => {
    const accessEndsAt = tx.effective_expires_at ?? tx.expires_at;
    const isActive =
      !!accessEndsAt && new Date(accessEndsAt).getTime() > Date.now();

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="border-b border-gray-200 pb-4 dark:border-gray-700">
          <div className="mb-2 flex items-center gap-2">
            <SiAppstore className="h-6 w-6 text-blue-600" />
            <h3 className="text-xl font-bold text-navy-700 dark:text-white">
              In-App Payment Details
            </h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Transaction ID: {tx.id}
          </p>
        </div>

        {/* Purchase Information */}
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
            Payment Information
          </h4>
          <div className="space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            <div>
              <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Product ID
              </div>
              <div className="font-medium text-navy-700 dark:text-white">
                {tx.product_id ? formatProductName(tx.product_id) : EMPTY_VALUE}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Store
                </div>
                <div
                  className={`inline-flex items-center rounded px-3 py-1.5 text-sm font-medium ${badge(
                    "blue"
                  )}`}
                >
                  <MdStore className="mr-1.5 h-4 w-4" />
                  {tx.store ? formatStoreLabel(tx.store) : EMPTY_VALUE}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Environment
                </div>
                <div
                  className={`inline-flex items-center rounded px-3 py-1.5 text-sm font-medium ${badge(
                    "gray"
                  )}`}
                >
                  {tx.environment ?? EMPTY_VALUE}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Price
                </div>
                <div className="text-xl font-bold text-navy-700 dark:text-white">
                  ${tx.price.toFixed(2)} {tx.currency}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Status
                </div>
                <div>
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1.5 text-sm font-semibold ${badge(
                      isActive || !accessEndsAt ? "green" : "gray"
                    )}`}
                  >
                    {(isActive || !accessEndsAt) && (
                      <MdCheckCircle className="mr-1 h-4 w-4" />
                    )}
                    {isActive ? "Active" : accessEndsAt ? "Expired" : "Paid"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
            Important Dates
          </h4>
          <div className="space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            {tx.purchased_at && (
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Purchased At
                </div>
                <div className="flex items-center text-navy-700 dark:text-white">
                  <MdCalendarToday className="mr-2 h-4 w-4 text-gray-400" />
                  {formatDate(tx.purchased_at)}
                </div>
              </div>
            )}
            {tx.expires_at && (
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Expires At
                </div>
                <div className="flex items-center text-navy-700 dark:text-white">
                  <MdCalendarToday className="mr-2 h-4 w-4 text-gray-400" />
                  {formatDate(tx.expires_at)}
                </div>
              </div>
            )}
            {tx.effective_expires_at &&
              tx.effective_expires_at !== tx.expires_at && (
                <div>
                  <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                    Grace Period Ends
                  </div>
                  <div className="flex items-center text-navy-700 dark:text-white">
                    <MdCalendarToday className="mr-2 h-4 w-4 text-gray-400" />
                    {formatDate(tx.effective_expires_at)}
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Subscription */}
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
            Subscription
          </h4>
          <div className="space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            <div>
              <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                RevenueCat Subscription ID
              </div>
              <div className="font-mono text-sm text-navy-700 dark:text-white">
                {tx.subscription_id}
              </div>
            </div>
            {tx.store_subscription_identifier && (
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Store Subscription ID
                </div>
                <div className="font-mono text-sm text-navy-700 dark:text-white">
                  {tx.store_subscription_identifier}
                </div>
              </div>
            )}
            {tx.ownership && (
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Ownership
                </div>
                <div className="font-medium text-navy-700 dark:text-white">
                  {formatLabel(tx.ownership)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // The plan a pending change moves the user onto. Nothing has been paid for it, so
  // there is no transaction id, no price from RevenueCat and no period to expire.
  const renderUpcomingPlanDetails = (plan: UpcomingPlan) => {
    const price = formatMoney(plan.amount, plan.currency);
    const startsAt = plan.starts_at ?? plan.current_period_starts_at;
    const endsAt = plan.ends_at ?? plan.current_period_ends_at;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="border-b border-gray-200 pb-4 dark:border-gray-700">
          <div className="mb-2 flex items-center gap-2">
            <MdSchedule className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            <h3 className="text-xl font-bold text-navy-700 dark:text-white">
              Upcoming Subscription
            </h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Takes effect at the end of the current period.
          </p>
        </div>

        {/* Plan */}
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
            Plan
          </h4>
          <div className="space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            <div>
              <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Product
              </div>
              <div className="font-medium text-navy-700 dark:text-white">
                {plan.product_name
                  ? formatProductName(plan.product_name)
                  : EMPTY_VALUE}
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Product ID
              </div>
              <div className="font-mono text-sm text-navy-700 dark:text-white">
                {formatProductName(
                  plan.store_product_id ?? plan.product_id ?? EMPTY_VALUE
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Store
                </div>
                <div
                  className={`inline-flex items-center rounded px-3 py-1.5 text-sm font-medium ${badge(
                    "blue"
                  )}`}
                >
                  <MdStore className="mr-1.5 h-4 w-4" />
                  {plan.store ? formatStoreLabel(plan.store) : EMPTY_VALUE}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Status
                </div>
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1.5 text-sm font-semibold ${badge(
                    "amber"
                  )}`}
                >
                  <MdSchedule className="mr-1 h-4 w-4" />
                  Upcoming
                </span>
              </div>
            </div>

            <div>
              <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Price
              </div>
              <div className="text-xl font-bold text-navy-700 dark:text-white">
                {price ?? EMPTY_VALUE}
              </div>
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
            Schedule
          </h4>
          <div className="space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Starts
                </div>
                <div className="flex items-center text-navy-700 dark:text-white">
                  <MdCalendarToday className="mr-2 h-4 w-4 text-gray-400" />
                  {startsAt ? formatDate(startsAt) : EMPTY_VALUE}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Ends
                </div>
                <div className="flex items-center text-navy-700 dark:text-white">
                  <MdCalendarToday className="mr-2 h-4 w-4 text-gray-400" />
                  {endsAt ? formatDate(endsAt) : EMPTY_VALUE}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Billing Period
                </div>
                <div className="font-medium text-navy-700 dark:text-white">
                  {formatBillingPeriod(plan.interval, plan.interval_count)}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Auto-Renew
                </div>
                <div className="font-medium text-navy-700 dark:text-white">
                  {plan.auto_renewal_status
                    ? formatLabel(plan.auto_renewal_status)
                    : EMPTY_VALUE}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Entitlements */}
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
            Entitlements
          </h4>
          <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            {plan.entitlements === null ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Not yet determined.
              </p>
            ) : plan.entitlements.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {plan.entitlements.map((entitlement) => (
                  <span
                    key={entitlement}
                    className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${badge(
                      "gray"
                    )}`}
                  >
                    {formatLabel(entitlement)}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Grants no access.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Transaction Details"
      icon={
        transaction.type === "purchase" ? (
          <MdShoppingCart className="h-6 w-6 text-biafit-primary" />
        ) : (
          <MdAutorenew className="h-6 w-6 text-biafit-primary" />
        )
      }
      size="3xl"
      layout="scroll"
      stickyHeader
    >
      {/* Modal content */}
      <div className="px-6 py-6">
        {"starts_at" in transaction.original
          ? renderUpcomingPlanDetails(transaction.original as UpcomingPlan)
          : transaction.provider === "stripe"
          ? renderStripeDetails(transaction.original as StripePurchase)
          : "original_transaction_id" in transaction.original
          ? renderRevenueCatDetails(
              transaction.original as RevenueCatSubscription
            )
          : renderRevenueCatTransactionDetails(
              transaction.original as RevenueCatTransaction
            )}
      </div>
    </ModalShell>
  );
};

export default TransactionDetailsModal;
